import type { ApiNotification } from "./notificationEntityShape.ts";

export const CATEGORY_CONNECT_KINDS = [
  "POST_CREATED",
  "PORTFOLIO_CREATED",
  "NEW_MEMBER_SKILLS",
  "PRIVATE_PROJECT_PUBLISHED",
  "PRELIMINARY_APPROVED",
  "MATERIALS_QUOTATION",
] as const;

export type CategoryConnectKind = (typeof CATEGORY_CONNECT_KINDS)[number];

const KIND_SET = new Set<string>(CATEGORY_CONNECT_KINDS);

function str(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined;
  if (typeof v === "string") {
    const t = v.trim();
    return t.length ? t : undefined;
  }
  return undefined;
}

function numId(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : null;
}

function looseNum(n: ApiNotification, keys: string[]): string | null {
  const rec = n as Record<string, unknown>;
  for (const k of keys) {
    const id = numId(rec[k]);
    if (id) return id;
  }
  return null;
}

function resolveMaterialQuotationNavigationId(n: ApiNotification): string | null {
  const sq = n.subQuotationPostulation?.subQuotation;
  const fromNested =
    numId(sq?.quotation?.id) ??
    numId(
      (sq as { quotation_id?: unknown } | null | undefined)?.quotation_id
    ) ??
    numId((sq as { quotationId?: unknown } | null | undefined)?.quotationId);
  return (
    numId(n.quotation?.id) ??
    fromNested ??
    looseNum(n, ["quotationId", "idQuotation", "quotation_id"])
  );
}

function resolvePrivateProjectNavigationId(n: ApiNotification): string | null {
  return (
    numId(n.project?.id) ??
    numId(
      n.postulacionPermiso?.proyecto?.id ??
        (n.postulacionPermiso as { proyectoId?: number } | undefined)?.proyectoId
    ) ??
    numId(n.comentario_proyecto?.proyecto?.id) ??
    numId(
      (n.comentario_proyecto as { project?: { id?: number } } | null | undefined)
        ?.project?.id
    ) ??
    numId((n as { proyecto?: { id?: number } }).proyecto?.id) ??
    looseNum(n, ["proyectoId", "projectId", "proyecto_id"])
  );
}

function normalizeKind(raw: string): CategoryConnectKind | null {
  const t = raw
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");
  if (KIND_SET.has(t)) return t as CategoryConnectKind;
  return null;
}

function parseCategoryConnectKindFields(n: ApiNotification): CategoryConnectKind | null {
  const rec = n as Record<string, unknown>;
  const fromTop = normalizeKind(str(rec.kind) ?? "");
  if (fromTop) return fromTop;
  const ui = n.userInfo;
  if (ui && typeof ui === "object") {
    const k = normalizeKind(str((ui as Record<string, unknown>).kind) ?? "");
    if (k) return k;
  }
  const msg = str(n.message);
  if (msg?.trim().startsWith("{")) {
    try {
      const o = JSON.parse(msg) as { kind?: unknown };
      const k = normalizeKind(str(o.kind) ?? "");
      if (k) return k;
    } catch {
      /* ignore */
    }
  }
  return null;
}

function inferCategoryConnectKind(n: ApiNotification): CategoryConnectKind | null {
  if (resolveMaterialQuotationNavigationId(n)) return "MATERIALS_QUOTATION";
  const postIdFromComment = (() => {
    const pid = n.comment_post?.post_id;
    if (pid === undefined || pid === null) return null;
    if (typeof pid === "object") return numId((pid as { id?: unknown }).id);
    return numId(pid as string | number);
  })();
  const postId = numId(n.post?.id) ?? postIdFromComment;
  if (postId) return "POST_CREATED";
  if (numId(n.portfolio?.id)) return "PORTFOLIO_CREATED";
  if (resolvePrivateProjectNavigationId(n)) return "PRIVATE_PROJECT_PUBLISHED";
  return null;
}

export function resolveCategoryConnectKind(
  n: ApiNotification
): CategoryConnectKind | null {
  return parseCategoryConnectKindFields(n) ?? inferCategoryConnectKind(n);
}

const ACTIVITY_BODY_FRAGMENT: Record<CategoryConnectKind, string> = {
  POST_CREATED: "publicó un nuevo post en la comunidad.",
  PORTFOLIO_CREATED: "publicó un nuevo portfolio.",
  NEW_MEMBER_SKILLS:
    "se sumó a la comunidad con rubros que coinciden con tus intereses.",
  PRIVATE_PROJECT_PUBLISHED: "publicó una obra privada.",
  PRELIMINARY_APPROVED: "tiene un anteproyecto aprobado.",
  MATERIALS_QUOTATION: "cargó materiales en una cotización privada.",
};

export function categoryConnectActivityBodyFragment(kind: CategoryConnectKind): string {
  return ACTIVITY_BODY_FRAGMENT[kind];
}

export function buildCategoryConnectDeeplink(n: ApiNotification): string | null {
  const kind = resolveCategoryConnectKind(n);
  const quotationId = resolveMaterialQuotationNavigationId(n);
  const projectId = resolvePrivateProjectNavigationId(n);
  const senderId = numId(n.userSender?.id);

  const profileOrExplore = () =>
    senderId ? `/perfil/${senderId}` : "/marketplace/explorar";

  const routeForKind = (k: CategoryConnectKind): string => {
    switch (k) {
      case "MATERIALS_QUOTATION":
        return quotationId
          ? `/cotizaciondemateriales/${quotationId}`
          : "/cotizaciondemateriales";
      case "PRIVATE_PROJECT_PUBLISHED":
      case "PRELIMINARY_APPROVED":
        return projectId ? `/obrasprivadas/${projectId}` : "/obrasprivadas";
      case "POST_CREATED":
        return "/marketplace/explorar";
      case "PORTFOLIO_CREATED":
      case "NEW_MEMBER_SKILLS":
        return profileOrExplore();
    }
  };

  if (kind) return routeForKind(kind);
  if (quotationId) return `/cotizaciondemateriales/${quotationId}`;
  if (projectId) return `/obrasprivadas/${projectId}`;
  return profileOrExplore();
}
